import { useEffect, useMemo, useState } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import * as apiClient from 'api/apiClients';
import type { ClientPayload } from 'api/apiClients';
import { useToast } from 'context/ToastContext';
import ESpinner from '../../others/Spinner';
import ClientEditForm from './ClientEditForm';

interface OffClientEditProps {
    clientId: number | null;
    setClientId: (clientId: number | null) => void;
    fetchClients: (force?: boolean) => Promise<void>;
}

const emptyClient: ClientPayload = {
    clientName: '',
    clientPhone: '',
    clientEmail: '',
    clientAddress: '',
    clientLocation: '',
    clientDescription: '',
    clientStatus: 0,
};

const OffClientEdit = ({ clientId, setClientId, fetchClients }: OffClientEditProps) => {
    const { t } = useTranslation(['Clients', 'common']);
    const { showToast } = useToast();
    const [client, setClient] = useState<ClientPayload>(emptyClient);
    const [loading, setLoading] = useState(false);
    const [loadingClient, setLoadingClient] = useState(false);
    const isCreate = clientId === 0;

    const title = useMemo(
        () => (isCreate ? t('edit.titleCreate') : t('edit.titleUpdate')),
        [isCreate, t],
    );

    const handleClose = () => {
        setClientId(null);
        setClient(emptyClient);
    };

    const fetchClient = async () => {
        if (!clientId || isCreate) return;

        setLoadingClient(true);
        const resClient = await apiClient.getClientById(clientId);
        setLoadingClient(false);

        if (!resClient) {
            showToast(t('common:messError'), 'danger');
            return;
        }

        setClient({
            clientName: resClient.clientName || '',
            clientPhone: resClient.clientPhone || '',
            clientEmail: resClient.clientEmail || '',
            clientAddress: resClient.clientAddress || '',
            clientLocation: resClient.clientLocation || '',
            clientDescription: resClient.clientDescription || '',
            clientStatus: resClient.clientStatus ?? 0,
        });
    };

    const handleSubmit = async (payload: ClientPayload) => {
        setLoading(true);

        try {
            if (isCreate) {
                const result = await apiClient.createClient(payload);
                if (result.ok === false) {
                    if (result.errCode === 'CLIENT_EMAIL_EXISTS') {
                        showToast(t('messClientEmailExists'), 'danger');
                        return;
                    }
                    showToast(t('common:messError'), 'danger');
                    return;
                }
            } else {
                const stat = await apiClient.updateClient(clientId as number, payload);
                if (!stat) {
                    showToast(t('common:messError'), 'danger');
                    return;
                }
            }

            showToast(t(isCreate ? 'messClientCreated' : 'messClientUpdated'), 'success');
            await fetchClients(true);
            handleClose();
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (clientId === null) return;
        if (isCreate) {
            setClient(emptyClient);
            return;
        }
        fetchClient();
    }, [clientId]);

    return (
        <Offcanvas show={clientId !== null} onHide={handleClose} placement="end">
            <Offcanvas.Header className="border-bottom" closeButton>
                <Offcanvas.Title>{title}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                {loadingClient ? (
                    <ESpinner />
                ) : (
                    <ClientEditForm
                        initialValues={client}
                        loading={loading}
                        submitLabel={isCreate ? t('toolAddClient') : t('common:buttons.save')}
                        onSubmit={handleSubmit}
                    />
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default OffClientEdit;
